import {Box, Center, createStyles, Loader} from '@mantine/core';
import React from 'react';

import {useValidProteinSchemasSummaryQuery} from 'antibodies/hooks/useValidProteinSchemasSummaryQuery';
import {TagSchema} from 'constants/RegistryConstants';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import QueryingTagSchemaSelector from 'schemas/shared/TagSchemaSelector';

// The schema selector's trigger button sizes to its label, which leaves it narrow for short schema
// names. Stretch it to fill the editor row (the selector exposes no width prop of its own).
const useStyles = createStyles({
  fullWidth: {
    width: '100%',
    '& .selectDropdown-button': {
      width: '100%',
    },
  },
});

interface ValidProteinSchemaEditorProps {
  value: string | null;
  onChange: (schemaId: string | null) => void;
  required: boolean;
  disabled?: boolean;
}

/**
 * Schema picker specialized to the registry's valid, unarchived protein schemas. When biologics is
 * disabled the underlying query is skipped and no schemas are offered.
 */
export default function ValidProteinSchemaEditor({
  value,
  onChange,
  required,
  disabled,
}: ValidProteinSchemaEditorProps): JSX.Element {
  const {classes} = useStyles();
  const {status, validProteinSchemas} = useValidProteinSchemasSummaryQuery();

  // While the schemas are still resolving, render a loading state directly rather than the picker: the
  // underlying selector's own loading indicator keys off its tag-schema query, which may already be
  // cached, so it would otherwise show an enabled, empty-filtered dropdown during this window.
  if (status === QueryResultType.PENDING) {
    return (
      <Center className={classes.fullWidth}>
        <Loader aria-label="Loading schemas" />
      </Center>
    );
  }

  const allowedIds = new Set(
    validProteinSchemas.filter((summary) => !summary.schema.archived).map((summary) => summary.schema.id)
  );
  return (
    <Box className={classes.fullWidth}>
      <QueryingTagSchemaSelector
        // Empty string is unset per the config contract; the selector expects a real id or null.
        selectedTagSchemaId={value || null}
        allowNone={!required}
        isDisabled={Boolean(disabled)}
        tagSchemaFilterFn={(schema: TagSchema): boolean => allowedIds.has(schema.id)}
        shouldDefaultIfSingleItem={!disabled}
        onChange={(schema: TagSchema | null) => {
          onChange(schema?.id ?? null);
        }}
        dataTestAttrs={{component: 'ValidProteinSchemaEditor', element: 'selectDropdown'}}
      />
    </Box>
  );
}
